import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

// Absolute instant formatting for the whole app (ADR-0007: "All formatting goes through one
// module (`lib/date`) … Features never call `dayjs` formatting directly").
//
// Every helper here takes a UTC ISO instant off the wire and renders it in the **viewer's local
// timezone**, which is ADR-0007's display rule. Formats are written as explicit dayjs patterns
// rather than `toLocaleDateString(locale, …)` on purpose: a locale argument decides field *order*
// for you, which is how "Jan 12" (en-US) and "12 Jan 2026" (en-GB) ended up side by side on the
// same page. Naming each pattern after the shape it produces keeps the design's chosen order and
// keeps it inspectable in one place.
//
// Elapsed/relative time lives in `./relative-time`; date-only calendar values (never
// tz-converted, per ADR-0007) stay in `./iso`.

dayjs.extend(advancedFormat);

/** Parse an instant, or `null` when absent/unparseable — callers omit the field rather than print junk. */
function instant(iso: string | null | undefined) {
  if (!iso) return null;
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed : null;
}

/** Hour of day — "12 AM", "2 PM". Used for the Today flow-rate axis (US-112437 §3). */
export function formatHour(iso: string | null | undefined): string | null {
  return instant(iso)?.format('h A') ?? null;
}

/** Month and day — "Jan 12". The 7 Days / 30 Days flow-rate axis (§3). */
export function formatMonthDay(iso: string | null | undefined): string | null {
  return instant(iso)?.format('MMM D') ?? null;
}

/** Month and year — "Jan 2025". The 1 Year / All Time flow-rate axis (§3). */
export function formatMonthYear(iso: string | null | undefined): string | null {
  return instant(iso)?.format('MMM YYYY') ?? null;
}

/** Day, month and year — "12 Jan 2026". The flow-rate tooltip heading (§3). */
export function formatDayMonthYear(iso: string | null | undefined): string | null {
  return instant(iso)?.format('D MMM YYYY') ?? null;
}

/**
 * Month, day and year — "Jan 12, 2024". The Subscription Details panel: its Purchased On /
 * Valid Till pair and every timeline stamp (US-113569 § C, Figma 518:522774).
 *
 * Month-first where {@link formatDayMonthYear} is day-first, and both are in use on the same
 * feature: the subscription **tile** reads "Expires on 10 Jan 2027" while the **panel** behind it
 * reads "Jan 12, 2024". US-113569 pins no format either way, so each surface keeps the order its
 * Figma frame draws — which is the case this module's header note exists for.
 */
export function formatMonthDayYear(iso: string | null | undefined): string | null {
  return instant(iso)?.format('MMM D, YYYY') ?? null;
}

/**
 * Day with an ordinal suffix — "12th Jan 2026". The Warranty Valid Till value (§6).
 *
 * `Do` comes from dayjs's `advancedFormat` plugin, so the 1st/2nd/3rd/11th/12th/13th special
 * cases are the library's rather than a hand-rolled modulo ladder.
 */
export function formatOrdinalDayMonthYear(iso: string | null | undefined): string | null {
  return instant(iso)?.format('Do MMM YYYY') ?? null;
}

/**
 * Day, month, year and clock time — "20 Jan 2025, 07:30 PM". The Activity Timeline stamp
 * (113581 §E, Figma 430:411872).
 *
 * The separator is a **comma**, not the ` | ` the admin twin's `formatWarrantyDateTime` uses —
 * that pipe belongs to a queue drawer's "Order Date & Time" field and contradicts both the AC and
 * all three frames here. Day is unpadded, per this module's convention.
 */
export function formatDayMonthYearTime(iso: string | null | undefined): string | null {
  return instant(iso)?.format('D MMM YYYY, hh:mm A') ?? null;
}
